/**
 * Where the caret goes when a dialog opens, and where it goes when one leaves.
 *
 * One module rather than a copy in the sheet and another in the palette, because the rule is
 * subtle in both directions and two copies would be two chances to get half of it right.
 *
 * **Arriving.** Radix focuses the first focusable element in the content, which is the close
 * button. That is right for a panel somebody opened to read and wrong for one they opened to
 * type into, so `autoFocusSelector` names a destination instead.
 *
 * **Leaving.** Radix returns focus to a `Dialog.Trigger`. Neither of this application's dialogs
 * has one — the sheets are opened from a button in the header or the phone bar, and the palette
 * is usually opened with Ctrl+K, from no element at all. So nothing was restored, and a keyboard
 * user who opened the outline and pressed Escape landed on `<body>`, with the next Tab starting
 * from the top of the document. Remembering what had focus is the fix, and it is what a
 * `Dialog.Trigger` would have done.
 *
 * **The opener is remembered from outside Radix**, because the obvious places both have a blind
 * spot, and they are the same blind spot seen from two sides: React applies `autoFocus` during
 * the commit, before any effect runs.
 *
 * - Reading `document.activeElement` in `onOpenAutoFocus` works for the sheets and never fires
 *   for the palette. Its search field carries `autoFocus`, so by the time Radix's focus scope
 *   asks "does anything inside already hold focus?" the answer is yes and it skips the mount
 *   event altogether.
 * - Listening for `focusin` and unsubscribing when the dialog opens records the *field* as the
 *   opener, because that same commit-time focus arrives before the effect that would have
 *   unsubscribed.
 *
 * So the guard is about *where* the focus landed rather than *when*: nothing inside a dialog is
 * ever a place to return to. That needs no knowledge of open and close order, and it holds for a
 * dialog opened from a button, from a shortcut, or from something not yet invented.
 *
 * There is one case where restoring is exactly wrong, and it is why `closeFocusSelector` exists:
 * when *closing is the interaction*. Adding a node from the outline drawer opens the new node for
 * typing on the canvas, underneath the sheet — the field takes focus, the focus trap takes it
 * straight back, and a restore on the way out would hand it to the button that was tapped. The
 * caret would end up two places from where it was asked for. A named destination wins over the
 * remembered opener; nothing else does.
 */
import { useEffect, useRef } from 'react';

export interface DialogFocusOptions {
  /** What should hold focus when the dialog arrives. Absent means "let Radix choose". */
  readonly autoFocusSelector?: string | undefined;
  /**
   * Where focus is going *because* the dialog closed — a field the closing action just opened.
   * Outranks the remembered opener. Absent means "give it back to whoever opened this".
   */
  readonly closeFocusSelector?: string | undefined;
}

export interface DialogFocusHandlers {
  readonly onOpenAutoFocus: (event: Event) => void;
  readonly onCloseAutoFocus: (event: Event) => void;
}

function elementFor(selector: string | undefined): HTMLElement | null {
  if (selector === undefined) return null;
  return document.querySelector<HTMLElement>(selector);
}

export function useDialogFocus(options: DialogFocusOptions = {}): DialogFocusHandlers {
  const { autoFocusSelector, closeFocusSelector } = options;
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const remember = (): void => {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) return;
      // `<body>` is "nothing has focus", which is not a place worth going back to — the element
      // remembered before it is a better answer than none.
      if (active === document.body) return;
      // Never a place to come back to. Anything in here is going away when the dialog does.
      if (active.closest('[role="dialog"]') !== null) return;
      opener.current = active;
    };

    remember();
    document.addEventListener('focusin', remember);
    return () => {
      document.removeEventListener('focusin', remember);
    };
  }, []);

  return {
    onOpenAutoFocus: (event) => {
      const target = elementFor(autoFocusSelector);
      if (target === null) return;
      event.preventDefault();
      target.focus();
    },

    onCloseAutoFocus: (event) => {
      const destination = elementFor(closeFocusSelector) ?? opener.current;
      // `isConnected`, because the opener can have unmounted while the dialog was up — a row's
      // button in a list the dialog's own action rewrote. Focusing a detached node silently sends
      // the caret to `<body>`, which is the thing this exists to prevent.
      if (destination?.isConnected !== true) return;
      event.preventDefault();
      destination.focus();
    },
  };
}
